import {
  Lead,
  GuideSection,
  P,
  UL,
  OL,
  LI,
  Term,
  GuideLink,
  Callout,
} from './guide-ui';

/**
 * Body for "GDPR & PII in logs" guide.
 * Accuracy: GDPR Article 5 (principles incl. data minimisation, storage
 * limitation), Article 32 (security of processing, pseudonymisation), Article 17
 * (right to erasure), Recital 49 (network/info security as legitimate interest).
 * IP addresses can be personal data (CJEU Breyer).
 */
export function GdprPiiInLogsGuideBody() {
  return (
    <>
      <Lead>
        Logs are full of personal data: IP addresses, user IDs, emails, sometimes
        far more, which means the GDPR applies to them. That creates a tension:
        security and operations want to keep logs, while data-protection law says
        minimise and don&apos;t keep personal data longer than needed. This guide
        explains how the GDPR applies to logs and how to handle PII responsibly.
      </Lead>

      <Callout title="Not legal advice">
        <p>
          This is general guidance, not legal advice. How the GDPR applies to your
          logs depends on your data, purposes and jurisdiction. Involve your DPO or
          legal counsel for specifics.
        </p>
      </Callout>

      <GuideSection id="logs-are-personal-data" title="Logs contain personal data">
        <P>
          Under the GDPR, <Term>personal data</Term> is any information relating to an
          identifiable person. Logs routinely contain it: usernames, user IDs, email
          addresses, and <Term>IP addresses</Term>, which EU case law (the CJEU{' '}
          <Term>Breyer</Term> ruling) treats as personal data in many contexts. So a
          log store is a store of personal data, and the GDPR&apos;s principles apply
          to it.
        </P>
      </GuideSection>

      <GuideSection id="principles" title="The GDPR principles that bite">
        <UL>
          <LI><Term>Data minimisation (Art. 5)</Term>: only log the personal data you actually need for the purpose.</LI>
          <LI><Term>Storage limitation (Art. 5)</Term>: don&apos;t keep personal data longer than necessary; define and justify a retention period.</LI>
          <LI><Term>Purpose limitation (Art. 5)</Term>: use the logs for the purpose you collected them for (e.g. security, troubleshooting).</LI>
          <LI><Term>Integrity &amp; confidentiality (Art. 5 &amp; Art. 32)</Term>: secure the logs: access control, encryption, and where appropriate pseudonymisation.</LI>
        </UL>
        <P>
          Helpfully, the GDPR explicitly recognises <Term>network and information
          security</Term> as a legitimate interest (Recital 49), a common legal basis
          for security logging, but that does not exempt you from the principles
          above.
        </P>
      </GuideSection>

      <GuideSection id="erasure" title="The right to erasure vs. immutable logs">
        <P>
          The <Term>right to erasure</Term> (Article 17, &quot;right to be
          forgotten&quot;) sits awkwardly with logs you deliberately keep
          tamper-evident for security and compliance. The usual resolution: security
          logging under a legitimate-interest or legal-obligation basis can justify
          retaining logs for a defined period despite an erasure request, and{' '}
          <Term>pseudonymisation</Term> or redaction reduces the personal-data
          footprint so erasure is less often required in the first place.
        </P>
      </GuideSection>

      <GuideSection id="how-to-handle" title="How to handle PII in logs">
        <OL>
          <LI><Term>Don&apos;t log what you don&apos;t need</Term>: keep secrets, full payloads and excess personal data out of logs at the source.</LI>
          <LI><Term>Redact or pseudonymise at ingest</Term>: mask or hash PII in a log pipeline before it is stored.</LI>
          <LI><Term>Set a justified retention period</Term>, and document the basis; see <GuideLink href="/guides/log-retention-requirements">log retention requirements</GuideLink>.</LI>
          <LI><Term>Secure the store</Term>: access control, encryption in transit and at rest, audited access.</LI>
          <LI><Term>Mind data residency</Term>: where EU data-protection drives your obligations, keeping log data in the EU simplifies compliance.</LI>
        </OL>
        <Callout title="Redaction beats deletion">
          <p>
            The cleanest way to reconcile security logging with the GDPR is to never
            store the sensitive value in the first place: redact or pseudonymise PII
            in the pipeline, so the log keeps its investigative value without holding
            unnecessary personal data.
          </p>
        </Callout>
      </GuideSection>

      <GuideSection id="how-logpulse-helps" title="How LogPulse helps with PII in logs">
        <P>
          LogPulse runs visual pipelines that can <Term>redact or mask PII before
          storage</Term>, supporting data minimisation and storage limitation. Log
          data stays in the EU (GCP Amsterdam) and AI evaluation runs in an EU region,
          which simplifies GDPR obligations when EU law drives them. See{' '}
          <GuideLink href="/guides/structured-logging">structured logging</GuideLink>{' '}
          for getting clean, field-level data that is easy to redact, and{' '}
          <GuideLink href="/guides/nis2-logging-requirements">NIS2</GuideLink> for the
          security-regulation side. LogPulse supports your compliance programme; it is
          not itself a certification.
        </P>
      </GuideSection>
    </>
  );
}
